import type { AnnouncementPlacementChoice } from '@gto/api-client/admin/react-query'

/**
 * Every placement the BE's enum accepts, ordered by how prominent the surface is
 * to a customer — the order `PLACEMENT_COLOR` in `placement-badge.tsx` already
 * reads in. Deliberately NOT the list's own `ordering=placement`, which sorts a
 * CharField lexicographically (dashboard_banner, modal, strip_banner, toaster)
 * and would put the rarest surface first in a menu people scan.
 *
 * The URL schema validates against this same list, which is what keeps a pasted
 * placement from reaching an endpoint that answers an unknown one with a 422.
 */
export const PLACEMENT_CHOICES = [
  'toaster',
  'strip_banner',
  'dashboard_banner',
  'modal',
] as const satisfies readonly AnnouncementPlacementChoice[]

/**
 * Staff-facing labels for each placement. Kept out of i18n because admin-web is
 * English-only staff tooling — see apps/admin-web/README.md.
 *
 * Lives here rather than beside the badge so the filter and the badge name a
 * placement identically: a dropdown reading "Strip banner" has to select the
 * rows whose badge says "Strip banner".
 */
export const PLACEMENT_LABEL: Record<AnnouncementPlacementChoice, string> = {
  toaster: 'Toaster',
  strip_banner: 'Strip banner',
  dashboard_banner: 'Dashboard banner',
  modal: 'Modal',
}

/**
 * The label for a placement the SERVER named, which this build may not know.
 *
 * The generated type promises one of four values, so an index into
 * `PLACEMENT_LABEL` reads as a string the compiler can prove. A backend that
 * ships a fifth surface before admin-web is rebuilt breaks that promise, and the
 * raw value names the surface well enough to keep a sentence true. Takes
 * `string` rather than the union so the gap is visible in the signature.
 *
 * Index `PLACEMENT_LABEL` directly for a placement the OPERATOR chose: that one
 * comes from this build's own list, so it is always a key, and the round trip
 * through here would only suggest otherwise.
 */
export const placementLabel = (placement: string): string => {
  const known = PLACEMENT_CHOICES.find((choice) => choice === placement)

  return known === undefined ? placement : PLACEMENT_LABEL[known]
}
